import type { CustomFeedEvent, FeedElement, FeedEntry, ToggleHideFeedEntryInput } from "../models/feed";
import { type ApiClient, createEntityStore, runStoreAction } from "./shared";

export type FeedEntity = CustomFeedEvent | FeedEntry;
export type FeedElementEntity = FeedElement;

export const createFeedStore = (apiClient: ApiClient) => {
	const useStore = createEntityStore<FeedEntity>();

	const getCustomFeedEvents = async (startIso: string, endIso: string): Promise<readonly FeedEntity[]> => {
		const state = useStore.getState();
		return runStoreAction(state.setLoading, state.setError, async () => {
			const items = await apiClient.get<FeedEntity[]>("/get/customfeedevent", {
				$filter: `start ge '${startIso}' and start le '${endIso}'`,
			});
			useStore.getState().upsertMany(items);
			return items;
		});
	};

	const getFeedElementsBetween = async (startIso: string, endIso: string): Promise<readonly FeedElementEntity[]> =>
		apiClient.get<FeedElementEntity[]>("/get/feedelement", {
			$filter: `start ge '${startIso}' and start le '${endIso}' and hideInFeed eq true`,
		});

	const getTimesWithOutlookReference = async (
		startIso: string,
		endIso: string,
		userId: string
	): Promise<readonly FeedEntity[]> =>
		apiClient.get<FeedEntity[]>("/get/times", {
			$filter: `start ge '${startIso}' and start le '${endIso}' and user__id eq '${userId}' and outlookCalenderReference ne ''`,
		});

	const toggleHideEntryInFeed = async (input: ToggleHideFeedEntryInput): Promise<void> => {
		const state = useStore.getState();
		await runStoreAction(state.setLoading, state.setError, async () => {
			if (input.shouldHide) {
				await apiClient.post<unknown>("/add/feedelement", {
					feedEventId: input.feedEventId,
					start: input.start,
					end: input.end,
					hideInFeed: true,
				});
				return;
			}

			const feedEventId = input.feedEventId.replace(/'/g, "''");
			const feedElements = await apiClient.get<Array<{ id: string }>>("/get/feedelement", {
				$filter: `feedEventId eq '${feedEventId}'`,
			});

			const entry = feedElements[0];
			if (entry?.id) {
				await apiClient.delete<unknown>(`/delete/feedelement/${entry.id}`);
			}
		});
	};

	return {
		useStore,
		actions: {
			getCustomFeedEvents,
			getFeedElementsBetween,
			getTimesWithOutlookReference,
			toggleHideEntryInFeed,
		},
	};
};
